import React, {useMemo} from 'react';
import {View} from 'react-native';
import {Text} from '../../../../../../../../shared/components/text';
import {useStyles} from '../../../../../../../../shared/hooks/styles';
import {datePipe, hexToRgbA} from '../../../../../../../../shared/pipes';
import {Props} from '../model';
import {BaseField} from '../../../../../../../../shared/components/baseField';
import {useRegCardState} from '../../../../../esiaRegCardState/useRegCardState';
import {TouchableOpacity} from 'react-native-gesture-handler';
import {capitalizeAndAddDot} from '../../../../../../../../shared/utils/text';
import {useIsSmallScreenDevice} from '../../../../../../../../shared/hooks/useIsSmallScreenDevice';
import {ParentSaturated} from '../../../../../esiaRegCardState/model';

export default ({kidId, userType}: Props) => {
  const {kids, parent, setKids, setParent} = useRegCardState();
  const isCardValid = useMemo(
    () => (userType === 'kid' && kidId != null && kids[kidId]?.errors?.length === 0) || userType === 'parent',
    [kidId, kids],
  );
  const isSmallScreen = useIsSmallScreenDevice();
  const styles = useStyles(({theme, addShadow}) => ({
    container: {
      backgroundColor: theme.color.primaryNonGreyBackground,
      borderRadius: 14,
      width: '100%',
      ...(isCardValid
        ? {
            ...addShadow({
              height: 2,
              radius: 5,
              opacity: 0.25,
            }),
          }
        : {}),
    },
    header: {
      paddingHorizontal: 10,
      position: 'relative',
      backgroundColor: isCardValid ? 'transparent' : hexToRgbA(theme.applications.mobileListItemBodyBg, 0.55),
      borderTopLeftRadius: 14,
      borderTopRightRadius: 14,
    },
    body: {
      borderBottomLeftRadius: 14,
      borderBottomRightRadius: 14,
      paddingHorizontal: 10,
      paddingVertical: 10,
      backgroundColor: isCardValid ? hexToRgbA(theme.applications.mobileListItemBodyBg, 0.15) : '#DDDDDD',
    },
    textNormal: {
      ...(isSmallScreen ? theme.fonts.bodySmallLight : theme.fonts.body),
      marginTop: 10,
    },
    textInvalidToApply: {
      ...theme.fonts.body,
      color: 'grey',
      marginTop: 10,
    },
    errorInfo: {
      ...theme.fonts.bodySmallLight,
      color: '#F15A59',
    },
    checkbox: {
      height: 42,
      width: 56,
      borderRadius: 15,
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      marginLeft: 'auto',
    },
    checkboxLable: {
      ...theme.fonts.bodySmallLight,
      marginTop: 10,
    },
  }));

  const handleParentCheckboxClick = (value: boolean) => {
    if (parent != null)
      setParent((prevParent) => {
        if (!prevParent) return prevParent;
        return {
          ...prevParent,
          checked: value,
        } as ParentSaturated;
      });
  };

  const handleKidCheckboxClick = (checkedKidId: number) => {
    kids[checkedKidId].checked = !kids[checkedKidId].checked;
    setKids([...kids]);
  };

  const parentFIO = `${parent?.firstName} ${parent?.middleName ? parent.middleName : ''} ${capitalizeAndAddDot(
    parent?.lastName,
  )}`;
  const childFIO =
    kidId != null && kids[kidId]
      ? `${kids[kidId]?.firstName} ${kids[kidId]?.middleName ? kids[kidId]?.middleName : ''} ${capitalizeAndAddDot(
          kids[kidId]?.lastName,
        )}`
      : '';
  const invalidKidMessageText = () => {
    if (kidId && kids[kidId]?.errors?.length) {
      return kids[kidId].errors.join(', ');
    }
  };

  const render = {
    parent: parent ? (
      <View style={styles.container}>
        <TouchableOpacity onPress={() => handleParentCheckboxClick(!parent?.checked)}>
          <View style={styles.header}>
            <Text style={styles.textNormal}>{parentFIO}</Text>
            <Text style={[styles.textNormal, {marginBottom: 10}]}>
              Дата рождения: {`${datePipe(parent?.birthDate)}`}
            </Text>
          </View>
          <View style={styles.body}>
            <View style={{display: 'flex', flexDirection: 'row'}}>
              <Text style={styles.textNormal}>Подать заявление на выдачу карты:</Text>
              <View style={styles.checkbox}>
                <BaseField
                  autoCapitalize='none'
                  input={{
                    value: parent.checked,
                  }}
                  type='checkbox'
                  onCustomBlur={() => {}}
                />
              </View>
            </View>
          </View>
        </TouchableOpacity>
      </View>
    ) : null,
    kid:
      kids && kidId != null ? (
        <View style={styles.container}>
          {isCardValid ? (
            <TouchableOpacity onPress={() => handleKidCheckboxClick(kidId)}>
              <View style={styles.header}>
                <Text style={styles.textNormal}>{childFIO}</Text>
                <Text style={[styles.textNormal, {marginBottom: 10}]}>
                  Дата рождения: {`${datePipe(kids[kidId]?.birthDate)}`}
                </Text>
              </View>
              <View style={styles.body} testID='bodyTest'>
                <View style={{display: 'flex', flexDirection: 'row'}}>
                  <Text style={styles.textNormal}>Подать заявление на выдачу карты:</Text>
                  <View style={styles.checkbox}>
                    <BaseField
                      autoCapitalize='none'
                      input={{
                        value: kids[kidId]?.checked,
                      }}
                      type='checkbox'
                      onCustomBlur={() => {}}
                      disabled={!isCardValid}
                    />
                  </View>
                </View>
              </View>
            </TouchableOpacity>
          ) : (
            <>
              <View style={styles.header}>
                <Text style={styles.textInvalidToApply}>{kids[kidId]?.firstName}</Text>
                <Text style={[styles.textInvalidToApply, {marginBottom: 10}]}>
                  Дата рождения: {`${datePipe(kids[kidId]?.birthDate)}`}
                </Text>
              </View>
              <View style={styles.body} testID='bodyTest'>
                <View style={{display: 'flex', flexDirection: 'row'}}>
                  <Text style={[styles.textInvalidToApply, {marginTop: 10}]}>Подать заявление на выдачу карты:</Text>
                  <View style={styles.checkbox} testID='test2'>
                    <BaseField
                      autoCapitalize='none'
                      input={{
                        value: kids[kidId]?.checked,
                      }}
                      type='checkbox'
                      onCustomBlur={() => {}}
                      disabled={!isCardValid}
                    />
                  </View>
                </View>
                <View>
                  <Text style={[styles.errorInfo, {marginTop: 10}]}>Отсутствуют: {invalidKidMessageText()}</Text>
                </View>
              </View>
            </>
          )}
        </View>
      ) : null,
  };
  return render[userType];
};
